import React from "react";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import "../css/privacy.scss";
import "../css/returns.scss";

const updatedDate = "2026-08-14";
const updatedDateLabel = "14 August 2026";

function Returns() {
  const returnSteps = [
    {
      title: "Tell us what is coming back",
      copy: `Contact ${siteConfig.contact.email} with your order number, the item and the reason for return. We will confirm the correct destination and instructions.`,
    },
    {
      title: "Pack it with care",
      copy: "Fold the unworn piece, attach every original tag and use secure packaging. Reusing the delivery mailer is encouraged when it is still protective.",
    },
    {
      title: "Choose a tracked service",
      copy: "Follow the return instructions and keep your carrier receipt and tracking number until the refund is complete. Return parcels remain your responsibility in transit unless we supplied the label.",
    },
    {
      title: "We inspect and refund",
      copy: "After arrival, our team checks the piece and emails the outcome. An approved refund is sent to the original payment method.",
    },
  ];

  const timingRows = [
    [
      "Request window",
      `Within ${siteConfig.commerce.returnWindow} days of the carrier-confirmed delivery date`,
      "Start the request before the window closes",
    ],
    [
      "Return transit",
      "Varies by origin and carrier",
      "Keep your tracking receipt",
    ],
    [
      "Warehouse review",
      "Usually 5–10 business days after arrival",
      "Busy periods may take a little longer",
    ],
    [
      "Payment posting",
      "Usually 2–10 business days after approval",
      "Timing is controlled by your bank or provider",
    ],
  ];

  const conditionRules = [
    "Unworn, unwashed and free from marks, scent, pet hair or alterations.",
    "All garment, care and security tags still attached in their original position.",
    "Shoes, accessories and sets returned with every component and protective packaging.",
    "Sent from the same order and within the confirmed return instructions.",
  ];

  const sections = [
    {
      id: "overview",
      title: "At a glance",
      content: (
        <>
          <p>
            We hope each piece becomes a long-term favourite. If it does not
            work for you, eligible items can be returned within{" "}
            {siteConfig.commerce.returnWindow} days of confirmed delivery. This
            policy sits alongside, and never replaces, mandatory consumer
            rights.
          </p>
          <dl className="policy-stat-grid">
            <div>
              <dt>Request window</dt>
              <dd>
                {siteConfig.commerce.returnWindow}
                <small>days</small>
              </dd>
            </div>
            <div>
              <dt>Item condition</dt>
              <dd>
                Original<small>unworn + tagged</small>
              </dd>
            </div>
            <div>
              <dt>Refund route</dt>
              <dd>
                Same<small>original payment method</small>
              </dd>
            </div>
          </dl>
          <aside className="policy-note" aria-label="Return reminder">
            <span className="eyebrow">Before posting</span>
            <p>
              Wait for return instructions so your parcel reaches the right team
              and can be matched to your order.
            </p>
          </aside>
        </>
      ),
    },
    {
      id: "eligibility",
      title: "What is eligible",
      content: (
        <>
          <p>
            A return must meet all of the following condition requirements
            unless the product arrived faulty:
          </p>
          <ul className="policy-check-list">
            {conditionRules.map((rule) => (
              <li key={rule}>{rule}</li>
            ))}
          </ul>
          <p>
            Trying on an item as you would in a store is fine. We may reduce or
            refuse a refund where handling goes beyond that and causes lost
            value, subject to applicable law. Keep the order email until the
            process is finished.
          </p>
        </>
      ),
    },
    {
      id: "process",
      title: "How to make a return",
      content: (
        <ol className="policy-steps">
          {returnSteps.map((step, index) => (
            <li key={step.title}>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <div>
                <h3>{step.title}</h3>
                <p>{step.copy}</p>
              </div>
            </li>
          ))}
        </ol>
      ),
    },
    {
      id: "costs",
      title: "Return costs & packaging",
      content: (
        <>
          <p>
            For a change-of-mind return, postage is normally deducted from the
            refund or paid directly to the carrier, and the exact amount is
            shown or confirmed before you proceed. Original delivery charges are
            refunded only where consumer law, a full-order cancellation or our
            error requires it.
          </p>
          <p>
            When we confirm that an item is faulty, damaged in transit or
            different from what you ordered, we cover reasonable return costs.
            Do not send parcels to the correspondence address without
            instructions; it may not be the returns facility.
          </p>
        </>
      ),
    },
    {
      id: "refunds",
      title: "Refund timing",
      content: (
        <>
          <p>
            Tracking may show delivered before a parcel is checked into our
            returns workflow. We will email once assessment is complete. All
            timing below is an estimate and can vary during peak periods.
          </p>
          <div
            className="policy-table-wrap"
            role="region"
            aria-label="Return and refund timing"
            tabIndex={0}
          >
            <table className="policy-table policy-table--timing">
              <thead>
                <tr>
                  <th scope="col">Stage</th>
                  <th scope="col">Typical timing</th>
                  <th scope="col">What to know</th>
                </tr>
              </thead>
              <tbody>
                {timingRows.map(([stage, timing, note]) => (
                  <tr key={stage}>
                    <th scope="row">{stage}</th>
                    <td>{timing}</td>
                    <td>{note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p>
            Banks and payment services control the final posting time. If the
            expected period has passed, reply to the refund confirmation so our
            team can check the transaction reference.
          </p>
        </>
      ),
    },
    {
      id: "exceptions",
      title: "Exceptions & non-returnable items",
      content: (
        <>
          <p>Where permitted by law, we cannot accept:</p>
          <ul className="policy-list policy-list--divided">
            <li>
              <strong>Personalised pieces</strong>
              <span>Items made or altered to your specification.</span>
            </li>
            <li>
              <strong>Hygiene-sensitive goods</strong>
              <span>
                Sealed underwear, swimwear or care products once the protective
                seal is removed.
              </span>
            </li>
            <li>
              <strong>Gift cards</strong>
              <span>
                Digital or physical stored-value cards, except where law
                requires a refund.
              </span>
            </li>
            <li>
              <strong>Used or altered goods</strong>
              <span>
                Pieces worn beyond fitting, washed, repaired, marked or missing
                original parts.
              </span>
            </li>
          </ul>
          <p>
            A sale price does not remove return rights: eligible discounted
            items follow the same condition and window unless they were clearly
            identified as final sale and that exclusion is lawful where you
            live.
          </p>
        </>
      ),
    },
    {
      id: "issues",
      title: "Faulty, damaged or incorrect",
      content: (
        <>
          <p>
            Please contact us as soon as you notice a problem. Include the order
            number, a short description, and clear photographs of the item,
            packaging and delivery label where relevant. Keep everything until
            we confirm the next step.
          </p>
          <p>
            Depending on availability and your legal rights, we may offer
            repair, replacement, price reduction or refund. Normal fibre
            variation, reasonable wear and damage caused by misuse or care
            contrary to the label are not manufacturing faults.
          </p>
        </>
      ),
    },
    {
      id: "exchanges",
      title: "Exchanges & help",
      content: (
        <>
          <p>
            Direct exchanges are not currently processed online. To secure
            another colour or size, place a new order and return the original
            eligible item separately. Each order must be packed and tracked
            under its own return instructions.
          </p>
          <address className="policy-address">
            <strong>{siteConfig.brandName} returns team</strong>
            <a href={`mailto:${siteConfig.contact.email}`}>
              {siteConfig.contact.email}
            </a>

            <span>{siteConfig.contact.address}</span>
            <span>{siteConfig.contact.hours}</span>
          </address>
        </>
      ),
    },
  ];

  return (
    <main
      className="returns policy-page policy-page--returns"
      aria-labelledby="returns-title"
    >
      <header className="policy-hero page-shell">
        <div className="policy-hero__label">
          <p className="eyebrow">Customer care · 03</p>
          <span>A considered way back.</span>
        </div>
        <div className="policy-hero__copy">
          <h1 className="page-heading" id="returns-title">
            Returns,
            <br />
            <em>made clear.</em>
          </h1>
          <p>
            {siteConfig.commerce.returnWindow} days to decide, with every step
            explained from request to refund.
          </p>
        </div>
        <div className="policy-hero__date">
          <span className="eyebrow">Policy updated</span>
          <time dateTime={updatedDate}>{updatedDateLabel}</time>
        </div>
      </header>

      <div className="policy-layout page-shell">
        <aside className="policy-toc">
          <div className="policy-toc__inner">
            <p className="eyebrow">Return guide</p>
            <nav aria-label="Returns policy sections">
              <ol>
                {sections.map((section, index) => (
                  <li key={section.id}>
                    <a href={`#${section.id}`}>
                      <span>{String(index + 1).padStart(2, "0")}</span>
                      {section.title}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>
            <div className="policy-toc__help">
              <span className="eyebrow">Start here</span>
              <a
                href={`mailto:${siteConfig.contact.email}?subject=Return%20request`}
              >
                Request a return <span aria-hidden="true">↗</span>
              </a>
            </div>
          </div>
        </aside>

        <article className="policy-content">
          {sections.map((section, index) => (
            <section
              className="policy-section"
              id={section.id}
              aria-labelledby={`${section.id}-title`}
              key={section.id}
            >
              <span className="policy-section__number" aria-hidden="true">
                {String(index + 1).padStart(2, "0")}
              </span>
              <div className="policy-section__body">
                <h2 id={`${section.id}-title`}>{section.title}</h2>
                {section.content}
              </div>
            </section>
          ))}
        </article>
      </div>

      <section
        className="policy-next page-shell"
        aria-labelledby="returns-next-title"
      >
        <div>
          <p className="eyebrow">Find the right fit</p>
          <h2 id="returns-next-title">
            The best return is the one you never need.
          </h2>
        </div>
        <div className="policy-next__links">
          <Link className="button-light" to="/shop">
            Return to the collection <span aria-hidden="true">→</span>
          </Link>
          <Link className="policy-next__text-link" to="/shipping">
            Read shipping details <span aria-hidden="true">↗</span>
          </Link>
        </div>
      </section>
    </main>
  );
}

export default Returns;
